import React, { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

import { Button } from 'devextreme-react/button';
import toast, { Toaster } from 'react-hot-toast';

import AntTextToSpeech from './AntTextToSpeech';
import AntTime from '../utils/AntTime';
import { AntGlobal } from '../utils/AntContext';

const SOUND_COOLDOWN_MS = 1000 * 4;

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const refSpeaker = useRef();
    const activeToasts = useRef([]);
    const dismissTimers = useRef(new Map());
    const lastSoundAt = useRef(0);
    const toastDuration = 1000 * 10;
    const maxVisibleToasts = 3;

    const dismissToast = useCallback((toastId) => {
        const timer = dismissTimers.current.get(toastId);
        if (timer) clearTimeout(timer);
        dismissTimers.current.delete(toastId);
        activeToasts.current = activeToasts.current.filter((item) => item.id !== toastId);
        toast.dismiss(toastId);
    }, []);

    const playSound = useCallback((data) => {
        const sounds = appGlobal.appAssets?.sounds;
        if (!sounds) return;

        let soundFile = sounds.sound_alert_4;
        const type = data?.sound_type;
        const isUrgent = type === 'OrderAlertUrgent';
        const now = Date.now();

        // Tin món có thể đến theo lô. Chỉ phát một âm thường trong mỗi cửa sổ,
        // nhưng không trì hoãn cảnh báo khẩn cấp (sự cố).
        if (!isUrgent && now - lastSoundAt.current < SOUND_COOLDOWN_MS) return;

        if (type === 'OrderAlertPaid' || type === 'OrderAlertCheckout') {
            soundFile = sounds.sound_alert_1;
        } else if (type === 'OrderAlertWarning') {
            soundFile = sounds.sound_alert_6;
        } else if (type === 'OrderAlertUrgent') {
            soundFile = sounds.sound_alert_7;
        }

        if (soundFile) {
            lastSoundAt.current = now;
            const audio = new Audio(soundFile);
            audio.play().catch((error) => console.error('Không thể phát âm thanh:', error));
        }
    }, [appGlobal.appAssets]);

    const speak = useCallback((text, times) => {
        if (refSpeaker.current?.canSpeak()) {
            refSpeaker.current.speak(text, times);
        }
    }, []);

    const renderToast = useCallback((t, data, onClicked) => {
        return (
            <div className="ant-pos-toast-content">
                <span
                    className="ant-pos-toast-text"
                    onClick={() => {
                        dismissToast(t.id);
                        if (onClicked) onClicked(data);
                    }}
                >
                    {data.text}
                </span>
                <Button
                    className="ant-pos-toast-icon ant-pos-toast-icon-info"
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={true}
                    activeStateEnabled={false}
                    onClick={() => dismissToast(t.id)}
                >
                    <i className="dx-icon dx-icon-close" />
                </Button>
            </div>
        );
    }, [dismissToast]);

    const showToast = useCallback(
        (data, onClicked, opts) => {
            if (opts?.playSound) playSound(data);
            if (opts?.speakVoice) {
                const textToSpeak = data?.textToSpeech ?? data?.text;
                if (textToSpeak) {
                    setTimeout(speak, 2000, textToSpeak, opts?.speakTimes);
                }
            }

            if (!opts?.showToast) return false;

            const toastId = `${Math.floor(Math.random() * 1000000)}${AntTime.nowUnix()}`;

            // Không để thông báo cũ chiếm hết màn hình POS khi socket gửi dồn dập.
            while (activeToasts.current.length >= maxVisibleToasts) {
                dismissToast(activeToasts.current[0].id);
            }

            let bgColor = '#501f85e6';
            if (data?.toast_type === 'Urgent') bgColor = '#982515d9';
            else if (data?.toast_type === 'Checkout') bgColor = '#b27604d9';
            if (data?.toast_bgColor) bgColor = data.toast_bgColor;

            toast((t) => renderToast(t, data, onClicked), {
                duration: toastDuration,
                id: toastId,
                style: {
                    borderRadius: '6px',
                    background: bgColor,
                    color: '#fff',
                    fontSize: '24px',
                    width: 'fit-content',
                    minWidth: '20vw',
                    maxWidth: '55vw',
                    ...data.style
                },
                className: data.className ?? 'ant-pos-toast'
            });

            activeToasts.current.push({ id: toastId });
            // `duration` của react-hot-toast là lớp tự ẩn chính; timer này là chốt
            // dọn độc lập để toast luôn biến mất, kể cả khi trạng thái render bị kẹt.
            dismissTimers.current.set(toastId, setTimeout(() => dismissToast(toastId), toastDuration));
            return true;
        },
        [dismissToast, renderToast, playSound, speak]
    );

    useEffect(() => {
        return () => {
            dismissTimers.current.forEach((timer) => clearTimeout(timer));
            dismissTimers.current.clear();
            activeToasts.current = [];
        };
    }, []);

    useImperativeHandle(ref, () => ({
        showToast: showToast
    }));

    return (
        <div>
            <Toaster position="bottom-left" reverseOrder={true} />
            <AntTextToSpeech ref={refSpeaker} rate={1.3} />
        </div>
    );
});
